import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, CATEGORIES } from "@/lib/config";
import { Lock, Play, Clock } from "lucide-react";

export default function LearningHub() {
  const [lessons, setLessons] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lessons");
        setLessons(data);
      } catch {}
    })();
  }, []);

  const visible = lessons.filter((l) => filter === "all" || l.category === filter);

  return (
    <AppShell>
      <div className="space-y-8">
        <div className="fade-up">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">Learning Hub</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">The library, curated.</h1>
          <p className="text-white/55 mt-2 max-w-2xl">
            Peptides, hormones, neuroplasticity, faith. Every lesson includes key takeaways, action steps, and a downloadable PDF.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 fade-up delay-1">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
            All
          </FilterChip>
          {CATEGORIES.map((c) => (
            <FilterChip key={c.key} active={filter === c.key} onClick={() => setFilter(c.key)} testid={`filter-${c.key}`}>
              {c.label}
            </FilterChip>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((l, i) => (
            <LessonCard key={l.lesson_id} lesson={l} index={i} />
          ))}
          {visible.length === 0 && (
            <div className="col-span-3 text-white/40 text-sm">No lessons yet.</div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-full text-xs tracking-wide transition-all ${
        active
          ? "bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-white"
          : "bg-[#0F0F0F] text-white/60 hover:text-white border border-white/5"
      }`}
    >
      {children}
    </button>
  );
}

function LessonCard({ lesson, index }) {
  const categoryColor = CATEGORIES.find((c) => c.key === lesson.category)?.accent || "#FF8A00";
  return (
    <Link
      to={`/learn/${lesson.lesson_id}`}
      data-testid={`lesson-card-${lesson.lesson_id}`}
      className={`group relative rounded-2xl overflow-hidden bg-[#0A0A0A] border border-white/5 hover:border-[#FF8A00]/30 hover:-translate-y-1 transition-all fade-up delay-${Math.min(index % 5 + 1, 5)}`}
    >
      <div className="aspect-video relative overflow-hidden">
        {lesson.thumbnail_url ? (
          <img
            src={lesson.thumbnail_url}
            alt={lesson.title}
            loading="lazy"
            data-testid={`lesson-thumb-${lesson.lesson_id}`}
            className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${
              lesson.unlocked ? "" : "grayscale opacity-70"
            }`}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-br"
             style={{
               background: lesson.thumbnail_url
                 ? "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0) 100%)"
                 : `radial-gradient(circle at 30% 30%, ${categoryColor}40, transparent 60%), linear-gradient(135deg, #151515, #080808)`
             }} />
        {!lesson.thumbnail_url && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        )}
        {!lesson.unlocked ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-black/60 border border-white/20 backdrop-blur flex items-center justify-center">
              <Lock size={20} className="text-white/70" />
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center ring-glow">
              <Play size={20} className="ml-0.5" />
            </div>
          </div>
        )}
        <div className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-semibold px-2.5 py-1 rounded-full backdrop-blur bg-black/40"
             style={{ color: categoryColor }}>
          {lesson.category}
        </div>
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 text-[10px] text-white/60 bg-black/50 px-2 py-1 rounded-full">
          <Clock size={10} /> {lesson.duration_min} min
        </div>
      </div>
      <div className="p-5">
        <div className="font-display text-xl leading-tight">{lesson.title}</div>
        <p className="text-xs text-white/50 mt-2 line-clamp-2">{lesson.description}</p>
        {!lesson.unlocked && (
          <div className="mt-3 text-[10px] tracking-[0.2em] uppercase text-[#E52E71]">
            Requires {lesson.tier_required}
          </div>
        )}
      </div>
    </Link>
  );
}
